import { useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "react-bootstrap-icons";
import { useListsContext } from "../ListsContext";
import {
  AddItemFields,
  AddItemRow,
  DisclosureButton,
  ItemTextInput,
  NoteInput,
  QuantityInput,
  SmallButton,
} from "../Lists.styled";

// Collapsing the form is remembered, so someone who wants the screen for the list on a
// phone gets it back every visit rather than collapsing it again each time.
const COLLAPSED_STORAGE_KEY = "ListsAddItemCollapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // Storage unavailable — remembering the choice is a convenience, not a requirement.
  }
}

// Text is required; quantity and note are optional. Enter submits from any field,
// since they all sit inside the one form element.
export function AddItemForm(props: { listId: string }) {
  const { addItem } = useListsContext();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [text, setText] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (text.trim() === "") {
      return;
    }
    addItem(props.listId, text, quantity, note);
    setText("");
    setQuantity("");
    setNote("");
    // Back to the name field so a run of items can be typed without reaching for it.
    nameRef.current?.focus();
  }

  return (
    <>
      <DisclosureButton
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-controls="add-item-fields"
      >
        {collapsed ? <ChevronRight /> : <ChevronDown />}
        Add Item
      </DisclosureButton>
      {/* Only the fields are unmounted. The component stays, so a half-typed item
          is still there after collapsing and expanding again. */}
      {!collapsed && (
        <AddItemFields id="add-item-fields" onSubmit={submit}>
          <AddItemRow>
            <ItemTextInput
              ref={nameRef}
              placeholder="Name"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
            <QuantityInput
              placeholder="Qty"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </AddItemRow>
          <AddItemRow>
            <NoteInput
              placeholder="Note (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <SmallButton type="submit">Add</SmallButton>
          </AddItemRow>
        </AddItemFields>
      )}
    </>
  );
}
